<script lang="ts">
  // Eine ziehbare Trennlinie (docs/shell.md).
  //
  // Es gibt vier davon -- Breite der Seitenleiste, Hoehe des unteren
  // Panels, Teilung der Arbeitsflaeche, Hoehe der angedockten Abschnitte
  // im Baum -- und sie brauchen alle
  // dasselbe: Zeigerfang, ein Schild waehrend des Zugs, Cursor,
  // Tastatur, ARIA. Drei Kopien davon waeren drei Stellen, an denen
  // spaeter eine Kleinigkeit fehlt (so wie es bei den Knopfhoehen
  // passiert ist).
  //
  // Was der Griff NICHT weiss: was gezogen wird. Er meldet die
  // Zeigerposition, der Aufrufer rechnet daraus seine Groesse -- der
  // Bezugspunkt ist bei jedem anders (linke Kante, Fensterhoehe,
  // Anteil an der Breite).
  //
  // Das Schild ist kein Zierrat: ohne es klaut der Inhalt die Maus,
  // sobald der Zeiger ihn streift, und der Zug reisst mitten in der
  // Bewegung ab -- bei eingebetteten Betrachtern zuverlaessig.

  interface Props {
    /** "x" = senkrechte Linie, wird nach links/rechts gezogen. */
    achse: "x" | "y";
    label: string;
    /** Nur fuer ARIA -- der Griff verwaltet den Wert nicht. */
    wert: number;
    min: number;
    max: number;
    onzug: (pos: { x: number; y: number }) => void;
    /** Pfeiltasten: Schrittweite in Pixeln, Vorzeichen wie die Achse. */
    onschritt: (delta: number) => void;
    /** Die drei Punkte nur, wo Platz zwischen zwei Kästen ist -- nicht auf
     *  einer Linie innerhalb eines Kastens (Abschnitte im Baum; Tim
     *  18.10.2026). */
    punkte?: boolean;
  }
  let { achse, label, wert, min, max, onzug, onschritt, punkte = true }: Props = $props();

  let zieht = $state(false);

  function start(e: PointerEvent) {
    zieht = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function bewegt(e: PointerEvent) {
    if (!zieht) return;
    onzug({ x: e.clientX, y: e.clientY });
  }
  function ende(e: PointerEvent) {
    if (!zieht) return;
    zieht = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }
  function taste(e: KeyboardEvent) {
    const runter = achse === "x" ? "ArrowLeft" : "ArrowDown";
    const hoch = achse === "x" ? "ArrowRight" : "ArrowUp";
    if (e.key === runter) onschritt(-24);
    else if (e.key === hoch) onschritt(24);
    else return;
    e.preventDefault();
  }
</script>

<!-- Eine ziehbare Trennlinie IST ein Bedienelement, kein Zierrat:
     fokussierbar, mit Wertebereich, per Pfeiltasten verstellbar. Genau
     das ist der dokumentierte ARIA-Weg fuer einen Splitter. svelte-check
     kennt "separator" nur als nicht-interaktiv und meldet deshalb zwei
     Falschtreffer -- unterdrueckt werden die Meldungen, nicht die
     Barrierefreiheit. -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="griff {achse}"
  class:zieht
  role="separator"
  aria-orientation={achse === "x" ? "vertical" : "horizontal"}
  aria-label={label}
  aria-valuenow={Math.round(wert)}
  aria-valuemin={min}
  aria-valuemax={max}
  tabindex="0"
  onpointerdown={start}
  onpointermove={bewegt}
  onpointerup={ende}
  onpointercancel={ende}
  onkeydown={taste}
>
  <!-- Drei Punkte in der Mitte: hier lässt sich ziehen (Tim 18.10.2026:
       „wo sich die Fenster ziehen lassen, diese 3 Punkte in der Mitte"). -->
  {#if punkte}<span class="punkte" aria-hidden="true"><i></i><i></i><i></i></span>{/if}
</div>

{#if zieht}
  <div class="schild {achse}"></div>
{/if}

<style>
  .griff {
    flex: none;
    touch-action: none;
    background: transparent;
    transition: background 0.12s ease;
    position: relative;
    z-index: 1;
  }
  /* Der Griff liegt im Abstand zwischen zwei Kaesten: keine Linie, die
     immer da ist (das waere wieder ein Strich zwischen den Kaesten) --
     nur die drei Punkte in der Mitte sagen, dass sich hier ziehen
     laesst. Die Flaeche faerbt sich erst, wenn man ihn anfasst. */
  .griff.x {
    width: 6px;
    cursor: col-resize;
    margin: 0 -1px;
  }
  .griff.y {
    height: 6px;
    cursor: row-resize;
    margin: -1px 0;
  }
  /* Ziehen geht SOFORT -- die Verzoegerung betrifft nur die Anzeige.
     Sonst waere der Griff eine Wartepflicht statt eines Angebots.
     Beim Verlassen verschwindet die Farbe ohne Verzoegerung: die
     Regel ohne :hover traegt kein transition-delay. */
  .griff:hover {
    background: var(--accent);
    transition-delay: 1s;
  }
  .griff.zieht,
  .griff:focus-visible {
    background: var(--accent);
    transition-delay: 0s;
    outline: none;
  }
  .punkte {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    pointer-events: none;
  }
  .x .punkte {
    flex-direction: column;
  }
  .punkte i {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--fg-subtle);
    opacity: 0.75;
    transition: background 0.12s ease;
  }
  .griff:hover .punkte i {
    transition-delay: 1s;
  }
  .griff:hover .punkte i,
  .griff.zieht .punkte i,
  .griff:focus-visible .punkte i {
    background: var(--accent-fg);
    opacity: 1;
  }
  .schild {
    position: fixed;
    inset: 0;
    z-index: 50;
  }
  .schild.x {
    cursor: col-resize;
  }
  .schild.y {
    cursor: row-resize;
  }
</style>
